import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, InputGroup, Table, Pagination, Badge, Modal } from 'react-bootstrap';
import SearchIcon from '@mui/icons-material/Search';
import { Page } from "../../dto/page/Page";
import { splitDate } from "../../app/Utils";
import { WalletWithdrawalRequestSearch } from "../../dto/wallet/WalletWithdrawalRequestSearch";
import { onWalletWithdrawalApprove, onWalletWithdrawalReject, onWalletWithdrawalRequestSearch } from "../../controller/WalletController";
import { WalletWithdrawalRequestEntity } from "../../dto/wallet/WalletWithdrawalRequestEntity";
import { WalletWithdrawalRequestListEntity } from "../../dto/wallet/WalletWithdrawalRequestListEntity";

// Local status -> badge color mapping (move into AppConst.ts to match your other badge helpers)
const getWithdrawalStatusBadge = (status?: string) => {
    switch (status) {
        case "PAID":
        case "APPROVED":
            return "success";
        case "REJECTED":
        case "CANCELLED":
        case "FAILED":
            return "danger";
        case "REQUESTED":
            return "warning";
        default:
            return "secondary";
    }
};

const WalletWithdrawalRequestList = () => {
    const navigate = useNavigate();
    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        }
    }, []);

    const [search, setSearch] = useState<WalletWithdrawalRequestSearch>({
        limit: 10
    });
    const [withdrawals, setWithdrawals] = useState<WalletWithdrawalRequestListEntity>({
        data: []
    });

    // ---- Search submit
    const searchSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        load(0); // reset to first page on new search
    };

    // ---- Pagination click
    const changePage = (page: number) => {
        if (!withdrawals.page) return;
        if (page < 0 || page >= withdrawals.page.totalPages) return;
        load(page);
    };

    const load = async (page: number = 0) => {
        const params: WalletWithdrawalRequestSearch = {
            ...search,
            offset: page
        };

        const response = await onWalletWithdrawalRequestSearch(params);

        if (response.status !== 0) {
            toast.error("Withdrawal request search unexpected error.");
            return;
        }

        const pageable = response.data;

        const pageInfo: Page = {
            limit: search.limit,
            totalElements: pageable.totalElements,
            totalPages: pageable.totalPages,
            offset: page
        };

        const beans: Array<WalletWithdrawalRequestEntity> = pageable.beans;

        setWithdrawals({
            page: pageInfo,
            data: beans
        });

        setSearch(prev => ({ ...prev, offset: page }));
    };

    // ---- Action modals
    const [actionRow, setActionRow] = useState<WalletWithdrawalRequestEntity | null>(null);
    const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const openAction = (row: WalletWithdrawalRequestEntity, type: "approve" | "reject") => {
        setActionRow(row);
        setActionType(type);
        setNote("");
    };

    const closeAction = () => {
        setActionRow(null);
        setActionType(null);
        setNote("");
    };

    const submitAction = async () => {
        if (!actionRow?.id || !actionType) return;

        setSubmitting(true);
        try {
            const response = actionType === "approve"
                ? await onWalletWithdrawalApprove(actionRow.id, note.trim() || undefined)
                : await onWalletWithdrawalReject(actionRow.id, note.trim() || undefined);

            if (response.status !== 0) {
                toast.error(response.msg ?? "Үйлдэл амжилтгүй.");
                return;
            }

            toast.success(actionType === "approve" ? "Баталгаажууллаа." : "Цуцаллаа.");
            closeAction();
            load(search.offset ?? 0); // refresh current page
        } catch (e) {
            toast.error("Үйлдэл амжилтгүй.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container fluid>
            <div><Toaster position="top-right" reverseOrder={false} /></div>
            <h3><a onClick={() => navigate(-1)}><big>&#8592;</big></a>Мөнгө татах хүсэлт</h3>
            <Row className="d-flex justify-content-center align-items-center mb-3">
                <Col md={12} lg={12} xs={12}>
                    <Card>
                        <Card.Header>
                            <Form onSubmit={searchSubmit}>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="nickname">
                                            <Form.Label>Nickname</Form.Label>
                                            <Form.Control size="sm" type="text"
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    if (value !== "") {
                                                        setSearch({
                                                            ...search,
                                                            nickname: value
                                                        });
                                                    } else {
                                                        const { nickname, ...rest } = search;
                                                        setSearch(rest);
                                                    }
                                                }} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="phoneNo">
                                            <Form.Label>User Phone</Form.Label>
                                            <Form.Control size="sm" type="text"
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    if (value !== "") {
                                                        setSearch({
                                                            ...search,
                                                            phoneNo: value
                                                        });
                                                    } else {
                                                        const { phoneNo, ...rest } = search;
                                                        setSearch(rest);
                                                    }
                                                }} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="limit">
                                            <Form.Label>Багцлах</Form.Label>
                                            <InputGroup className="mb-3">
                                                <Form.Select size="sm"
                                                    onChange={(event) => setSearch({
                                                        ...search,
                                                        limit: parseInt(event.target.value)
                                                    })}>
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="0">Бүгд</option>
                                                </Form.Select>
                                                <Button variant="primary" size="sm" type="submit">
                                                    <SearchIcon fontSize="small" />
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Requested at</th>
                                            <th>Created at</th>
                                            <th>Updated at</th>
                                            <th>Phone No</th>
                                            <th>Nickname</th>
                                            <th>Amount</th>
                                            <th>Currency</th>
                                            <th>Status</th>
                                            <th>Bank account name</th>
                                            <th>Account No</th>
                                            <th>IBAN</th>
                                            <th>User note</th>
                                            <th>Admin note</th>
                                            <th>Approved at</th>
                                            <th>Rejected at</th>
                                            <th>Cancelled at</th>
                                            <th>Paid at</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {withdrawals.data?.length === 0 && (
                                            <tr>
                                                <td className="text-muted">
                                                    Илэрц олдсонгүй
                                                </td>
                                            </tr>
                                        )}
                                        {
                                            withdrawals.data?.map((data, idx) => {
                                                return (
                                                    <tr key={idx}>
                                                        <td>{(splitDate(data.requestedAt!))?.date} {(splitDate(data.requestedAt!))?.time}</td>
                                                        <td>{(splitDate(data.createdAt!))?.date} {(splitDate(data.createdAt!))?.time}</td>
                                                        <td>{(splitDate(data.updatedAt!))?.date} {(splitDate(data.updatedAt!))?.time}</td>
                                                        <td>
                                                            <a href={`/user/${data.accountPhoneNo}`} target="_blank">
                                                                {data.accountPhoneNo ?? data.accountId}
                                                            </a>
                                                        </td>
                                                        <td>{data.accountNickname}</td>
                                                        <td>{data.amount?.toLocaleString()}</td>
                                                        <td>{data.currency}</td>
                                                        <td>
                                                            <Badge bg={getWithdrawalStatusBadge(data.status)}>
                                                                {data.status}
                                                            </Badge>
                                                        </td>
                                                        <td>{data.accountNameSnapshot}</td>
                                                        <td>{data.accountNoSnapshot}</td>
                                                        <td>{data.ibanSnapshot}</td>
                                                        <td>{data.userNote}</td>
                                                        <td>{data.adminNote}</td>
                                                        <td>{(splitDate(data.approvedAt!))?.date} {(splitDate(data.approvedAt!))?.time}</td>
                                                        <td>{(splitDate(data.rejectedAt!))?.date} {(splitDate(data.rejectedAt!))?.time}</td>
                                                        <td>{(splitDate(data.cancelledAt!))?.date} {(splitDate(data.cancelledAt!))?.time}</td>
                                                        <td>{(splitDate(data.paidAt!))?.date} {(splitDate(data.paidAt!))?.time}</td>
                                                        <td>
                                                            {data.status === "REQUESTED" && (
                                                                <div className="d-flex gap-1">
                                                                    <Button
                                                                        variant="success"
                                                                        size="sm"
                                                                        onClick={() => openAction(data, "approve")}
                                                                    >
                                                                        Approve
                                                                    </Button>
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => openAction(data, "reject")}
                                                                    >
                                                                        Reject
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                {withdrawals.page && withdrawals.page.totalPages > 1 && (() => {
                    const page = withdrawals.page;   // now TypeScript knows it's NOT undefined

                    return (
                        <Row>
                            <Col className="d-flex justify-content-start mt-3">
                                <Pagination>
                                    <Pagination.First
                                        disabled={page.offset === 0}
                                        onClick={() => changePage(0)}
                                    />

                                    <Pagination.Prev
                                        disabled={page.offset === 0}
                                        onClick={() => changePage(page.offset - 1)}
                                    />

                                    {Array.from({ length: page.totalPages })
                                        .slice(
                                            Math.max(page.offset - 2, 0),
                                            Math.min(page.offset + 3, page.totalPages)
                                        )
                                        .map((_, idx) => {
                                            const pageNumber =
                                                Math.max(page.offset - 2, 0) + idx;

                                            return (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={pageNumber === page.offset}
                                                    onClick={() => changePage(pageNumber)}
                                                >
                                                    {pageNumber + 1}
                                                </Pagination.Item>
                                            );
                                        })}

                                    <Pagination.Next
                                        disabled={page.offset + 1 >= page.totalPages}
                                        onClick={() => changePage(page.offset + 1)}
                                    />

                                    <Pagination.Last
                                        disabled={page.offset + 1 >= page.totalPages}
                                        onClick={() => changePage(page.totalPages - 1)}
                                    />
                                </Pagination>
                            </Col>
                        </Row>
                    );
                })()}
            </Row>

            <Modal show={actionRow !== null} onHide={closeAction} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {actionType === "approve" ? "Хүсэлт баталгаажуулах" : "Хүсэлт цуцлах"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <small className="text-muted">
                            ID: {actionRow?.id} — {actionRow?.accountNickname ?? actionRow?.accountId} — {actionRow?.amount?.toLocaleString()} {actionRow?.currency}
                        </small>
                    </div>
                    <Form.Group controlId="actionNote">
                        <Form.Label>Тэмдэглэл</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={note}
                            placeholder="Тэмдэглэл (заавал биш)..."
                            onChange={(event) => setNote(event.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={closeAction} disabled={submitting}>
                        Болих
                    </Button>
                    <Button
                        variant={actionType === "approve" ? "success" : "danger"}
                        size="sm"
                        onClick={submitAction}
                        disabled={submitting}
                    >
                        {submitting ? "..." : (actionType === "approve" ? "Баталгаажуулах" : "Цуцлах")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default WalletWithdrawalRequestList;